import { useCallback, useEffect, useState } from 'react';

import { determineMediaType, normalizeMashWidth } from '@pkg/components';

import { getMashedUrl } from '@app/helpers';

import {
    type MediaEventHandler,
    type VisualAssetEvent,
    type VisualAssetEventHandler,
    VisualAssetType,
} from '../../types';

export interface VisualAssetStateOptions {
    videoUrl?: string;
    imageUrl?: string;
    mashWidth?: number;
    onError?: VisualAssetEventHandler;
    onLoad?: VisualAssetEventHandler;
}

export function useVisualAssetState({
    videoUrl = '',
    imageUrl = '',
    mashWidth = 0,
    onError,
    onLoad,
}: VisualAssetStateOptions) {
    const [videoError, setVideoError] = useState(false);
    const [videoLoaded, setVideoLoaded] = useState(false);

    const [imageError, setImageError] = useState(false);
    const [imageLoaded, setImageLoaded] = useState(false);

    const errorHandler: MediaEventHandler = useCallback(
        ({ target, timeStamp }) => {
            const type = determineMediaType(target);
            const event: VisualAssetEvent = {
                type,
                target,
                timeStamp,
            };

            onError?.(event);

            if (type === VisualAssetType.Video) {
                setVideoError(true);
                setVideoLoaded(false);
            } else {
                setImageError(true);
                setImageLoaded(false);
            }
        },
        [onError],
    );

    const loadedHandler: MediaEventHandler = useCallback(
        ({ target, timeStamp }) => {
            const type = determineMediaType(target);
            const event: VisualAssetEvent = {
                type,
                target,
                timeStamp,
            };

            onLoad?.(event);

            if (type === VisualAssetType.Video) {
                setVideoError(false);
                setVideoLoaded(true);
            } else {
                setImageError(false);
                setImageLoaded(true);
            }
        },
        [onLoad],
    );

    const normalizedWidth = normalizeMashWidth(mashWidth);

    // TODO: Remove `?` once we are confident `null` values
    // are no longer getting passed without type-checking.
    const mashedVideo = videoUrl?.length
        ? getMashedUrl({
              url: videoUrl,
              width: normalizedWidth,
          })
        : undefined;

    // TODO: Remove `?` once we are confident `null` values
    // are no longer getting passed without type-checking.
    const mashedImage = imageUrl?.length
        ? getMashedUrl({
              url: imageUrl,
              width: normalizedWidth,
          })
        : undefined;

    const hasVideo = Boolean(mashedVideo?.length);
    const hasImage = Boolean(mashedImage?.length);

    const bothErrored = videoError && imageError;

    const stillLoadingImage = !imageError && !imageLoaded;
    const stillLoadingVideo = (!videoError && !videoLoaded) || (videoError && stillLoadingImage);

    useEffect(() => {
        setVideoError(!mashedVideo?.length);
        setVideoLoaded(false);
    }, [mashedVideo]);

    useEffect(() => {
        // This can lead to an abrupt switch between
        // `video > poster` values if the `video` doesn't
        // use `autoPlay` and hasn't been started yet.
        if (!videoError && videoLoaded) return;

        setImageError(!mashedImage?.length);
        setImageLoaded(false);
    }, [mashedImage, videoError, videoLoaded]);

    return {
        mashedVideo,
        mashedImage,
        errorHandler,
        loadedHandler,
        videoError,
        videoLoaded,
        imageError,
        imageLoaded,
        hasSource: hasVideo || hasImage,
        showError: hasVideo ? bothErrored : imageError,
        showLoading: hasVideo ? stillLoadingVideo : stillLoadingImage,
    };
}
